import React, { useEffect, useRef, useState } from "react";
import "../Styles/BackgroundInfoSection.css";

// ======================================================
// MONTHS
// ======================================================

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// ======================================================
// HELPERS
// ======================================================

const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const getDateParts = (date) => {
  if (!date) {
    return null;
  }

  let parsedDate = date;

  if (typeof date === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);

    if (!match) {
      return null;
    }

    const [, year, month, day] = match.map(Number);
    parsedDate = new Date(year, month - 1, day);

    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return null;
    }
  }

  if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) {
    return null;
  }

  return {
    day: parsedDate.getDate(),
    month: parsedDate.getMonth(),
    year: parsedDate.getFullYear(),
  };
};

// ======================================================
// COMPONENT
// ======================================================

export default function BackgroundInfoSection({
  dob,
  setDob,
  countryList,
  addresDet,
  handelAddreesDet,
  communityList,
  selectedResidence,
  handleResidence,
  selectedCitizenship,
  handleCitizenship,
  seletedEthinicity,
  handleEthinicity,
  postalcode,
  handlePostalcode,
}) {
  const [open, setOpen] = useState(false);

  // ----------------------------------------------------
  // 18 YEARS OLD MAXIMUM DATE
  // ----------------------------------------------------

  const today = new Date();

  const maxDob = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );

  // ----------------------------------------------------
  // INITIAL DOB
  // ----------------------------------------------------

  const initialDateParts = getDateParts(dob);

  const [selectedDay, setSelectedDay] = useState(initialDateParts?.day || 1);

  const [selectedMonth, setSelectedMonth] = useState(
    initialDateParts?.month ?? 0,
  );

  const [selectedYear, setSelectedYear] = useState(
    initialDateParts?.year || maxDob.getFullYear(),
  );

  // ----------------------------------------------------
  // UPDATE PICKER WHEN DOB CHANGES FROM PARENT
  // ----------------------------------------------------

  useEffect(() => {
    const parts = getDateParts(dob);

    if (parts) {
      setSelectedDay(parts.day);
      setSelectedMonth(parts.month);
      setSelectedYear(parts.year);
    }
  }, [dob]);

  // ----------------------------------------------------
  // YEARS
  // ----------------------------------------------------

  const MIN_YEAR = 1900;
  const MAX_YEAR = maxDob.getFullYear();

  const years = [];

  for (let year = MAX_YEAR; year >= MIN_YEAR; year--) {
    years.push(year);
  }

  // ----------------------------------------------------
  // DAYS
  // ----------------------------------------------------

  const daysInSelectedMonth = getDaysInMonth(selectedYear, selectedMonth);

  const days = Array.from(
    { length: daysInSelectedMonth },
    (_, index) => index + 1,
  );

  // ----------------------------------------------------
  // CLOSE PICKER
  // ----------------------------------------------------

  const closePicker = () => {
    setOpen(false);
  };

  // ----------------------------------------------------
  // CREATE DATE
  // ----------------------------------------------------

  const updateDOB = (day, month, year) => {
    const numberOfDays = getDaysInMonth(year, month);

    // If changing month/year makes the selected day invalid,
    // automatically move it to the last valid day.
    const validDay = Math.min(day, numberOfDays);

    const newDate = new Date(year, month, validDay);

    // Do not allow a DOB after the 18-year-old limit.
    if (newDate > maxDob) {
      return;
    }

    setSelectedDay(validDay);
    setSelectedMonth(month);
    setSelectedYear(year);

    setDob(newDate);
  };

  // ----------------------------------------------------
  // DAY CHANGE
  // ----------------------------------------------------

  const handleDayChange = (day) => {
    updateDOB(day, selectedMonth, selectedYear);
  };

  // ----------------------------------------------------
  // MONTH CHANGE
  // ----------------------------------------------------

  const handleMonthChange = (month) => {
    updateDOB(selectedDay, month, selectedYear);
  };

  // ----------------------------------------------------
  // YEAR CHANGE
  // ----------------------------------------------------

  const handleYearChange = (year) => {
    updateDOB(selectedDay, selectedMonth, year);
  };

  // ----------------------------------------------------
  // FORMAT DOB
  // ----------------------------------------------------

  const formatDOB = () => {
    const dateParts = getDateParts(dob);

    if (!dateParts) {
      return "";
    }

    const day = String(dateParts.day).padStart(2, "0");
    const month = String(dateParts.month + 1).padStart(2, "0");
    const year = dateParts.year;

    return `${day}-${month}-${year}`;
  };

  // ======================================================
  // WHEEL PICKER COLUMN
  // ======================================================

  const WheelColumn = ({ items, selectedValue, onChange, type }) => {
    const containerRef = useRef(null);

    const ITEM_HEIGHT = 40;

    // Scroll selected item into the center when picker opens.
    useEffect(() => {
      if (!open || !containerRef.current) {
        return;
      }

      const selectedIndex = items.findIndex(
        (item) => item.value === selectedValue,
      );

      if (selectedIndex === -1) {
        return;
      }

      containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
    }, [open, selectedValue, items]);

    const handleScroll = () => {
      if (!containerRef.current) {
        return;
      }

      const scrollTop = containerRef.current.scrollTop;

      const index = Math.round(scrollTop / ITEM_HEIGHT);

      const safeIndex = Math.max(0, Math.min(index, items.length - 1));

      const item = items[safeIndex];

      if (item && item.value !== selectedValue) {
        onChange(item.value);
      }
    };

    return (
      <div className={`dob-wheel-column dob-wheel-${type}`}>
        <div
          ref={containerRef}
          className="dob-wheel-scroll"
          onScroll={handleScroll}
        >
          {/* Top spacing */}
          <div className="dob-wheel-spacer" />

          {items.map((item) => (
            <div
              key={item.value}
              className={`dob-wheel-item ${
                item.value === selectedValue ? "dob-wheel-item-selected" : ""
              }`}
              onClick={() => onChange(item.value)}
            >
              {item.label}
            </div>
          ))}

          {/* Bottom spacing */}
          <div className="dob-wheel-spacer" />
        </div>
      </div>
    );
  };

  // ======================================================
  // WHEEL DATA
  // ======================================================

  const dayItems = days.map((day) => ({
    value: day,
    label: String(day).padStart(2, "0"),
  }));

  const monthItems = MONTHS.map((month, index) => ({
    value: index,
    label: month,
  }));

  const yearItems = years.map((year) => ({
    value: year,
    label: String(year),
  }));

  // ======================================================
  // RENDER
  // ======================================================

  return (
    <section className="background-section">
      <header className="background-section__header">
        <h2 className="background-section__title">Background Information</h2>

        <div className="background-section__divider" />
      </header>

      {/* ==================================================
          DOB
      ================================================== */}

      <div className="background-section__row">
        <label className="background-section__label" htmlFor="dob">
          Enter Date of Birth *
        </label>

        <div className="date-input-wrapper">
          <div
            className="custom-date-input dob-display-input"
            onClick={() => setOpen(true)}
          >
            <span className={dob ? "" : "dob-placeholder"}>
              {formatDOB() || "dd-mm-yyyy"}
            </span>
          </div>

          <img
            className="calender-icon"
            src={require("../assests/images/calender-icon.png")}
            alt="calendar"
            onClick={() => setOpen(true)}
          />
        </div>

        {/* ==================================================
            DOB WHEEL MODAL
        ================================================== */}

        {open && (
          <div className="dob-picker-overlay" onClick={closePicker}>
            <div
              className="dob-picker-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="dob-picker-header">
                <span>Select Date of Birth</span>

                <button
                  type="button"
                  className="dob-picker-close"
                  onClick={closePicker}
                >
                  ×
                </button>
              </div>

              {/* Column headings */}
              <div className="dob-wheel-headings">
                <div>Day</div>
                <div>Month</div>
                <div>Year</div>
              </div>

              {/* Wheel picker */}
              <div className="dob-wheel-wrapper">
                <WheelColumn
                  type="day"
                  items={dayItems}
                  selectedValue={selectedDay}
                  onChange={handleDayChange}
                />

                <WheelColumn
                  type="month"
                  items={monthItems}
                  selectedValue={selectedMonth}
                  onChange={handleMonthChange}
                />

                <WheelColumn
                  type="year"
                  items={yearItems}
                  selectedValue={selectedYear}
                  onChange={handleYearChange}
                />

                {/* Center selection indicator */}
                <div className="dob-wheel-selection-line" />
              </div>

              {/* Selected DOB */}
              <div className="dob-selected-value">
                {String(selectedDay).padStart(2, "0")}-
                {String(selectedMonth + 1).padStart(2, "0")}-{selectedYear}
              </div>

              <button
                type="button"
                className="dob-picker-done"
                onClick={closePicker}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ==================================================
          ADDRESS
      ================================================== */}

      <div className="cpf-title mb-4">Address</div>

      <div className="background-section__row">
        <label className="background-section__label mt-3" htmlFor="homeTown">
          Enter Address Details *
        </label>

        <input
          name="AddressDetails"
          placeholder="Address Details"
          className="background-section__input"
          value={addresDet}
          onChange={handelAddreesDet}
        />

        {/* ==================================================
            RESIDENCE
        ================================================== */}

        <label className="background-section__label mt-3" htmlFor="homeTown">
          Select Residence *
        </label>

        <select
          className="background-section__input"
          value={selectedResidence}
          onChange={handleResidence}
        >
          <option value="">Select Residence</option>

          {countryList?.map((contry) => (
            <option key={contry.id} value={contry.id}>
              {contry.CountryName} ({contry.CountryCode})
            </option>
          ))}
        </select>

        {/* ==================================================
            CITIZENSHIP
        ================================================== */}

        <label className="background-section__label mt-3" htmlFor="homeTown">
          Select Citizenship *
        </label>

        <select
          className="background-section__input"
          value={selectedCitizenship}
          onChange={handleCitizenship}
        >
          <option value="">Select Citizenship</option>

          {countryList?.map((contry) => (
            <option key={contry.id} value={contry.id}>
              {contry.CountryName} ({contry.CountryCode})
            </option>
          ))}
        </select>

        {/* ==================================================
            ETHNICITY
        ================================================== */}

        <label className="background-section__label mt-3" htmlFor="homeTown">
          Select Ethinicity
        </label>

        <select
          className="background-section__input"
          value={seletedEthinicity}
          onChange={handleEthinicity}
        >
          <option value="">Select Ethinicity</option>

          {communityList?.map((city) => (
            <option key={city.CommunitiesId} value={city.CommunitiesId}>
              {city.CommunitiesName}
            </option>
          ))}
        </select>

        {/* ==================================================
            POSTAL CODE
        ================================================== */}

        <label className="background-section__label mt-3" htmlFor="homeTown">
          Enter Postal Code
        </label>

        <input
          type="text"
          value={postalcode}
          onChange={handlePostalcode}
          className="background-section__input"
          placeholder="Enter Postal Code"
        />
      </div>
    </section>
  );
}
